/**
 * Issue Type Classifier
 * ---------------------
 * Derives a canonical IssueType from real signals (blocker category, affected
 * area, description keywords) instead of asking operators to pick from a
 * generic catch-all dropdown.
 *
 * Lives in src/data/situational/ per the Situational Awareness Layer rule:
 * any logic that gates UI based on entity state belongs here, not inlined.
 */
import type {
  IssueType,
  IssueBlockerCategory,
  IssueAffectedArea,
} from "@/data/types";

export interface ClassifyIssueInput {
  blockerCategory?: IssueBlockerCategory | "";
  affectedArea?: IssueAffectedArea | "";
  description?: string;
}

export interface ClassifyIssueResult {
  /** Canonical IssueType to persist on the Issue record. */
  issueType: IssueType;
  /** Human-readable label shown to operators (more specific than IssueType). */
  label: string;
  /** Short rationale shown under the label so operators trust the derivation. */
  rationale: string;
  /** True when the classifier had enough signal to be confident. */
  confident: boolean;
}

const KEYWORDS: Array<{
  test: RegExp;
  type: IssueType;
  label: string;
  rationale: string;
}> = [
  { test: /permit|inspection|municipal|hoa/i, type: "Permit Issue", label: "Permit Stall", rationale: "Permit / inspection signal in description" },
  { test: /no.?show|unavailable|reschedul|access|lockout|gate|key/i, type: "Scheduling Issue", label: "Access / Scheduling Gap", rationale: "Access or scheduling signal in description" },
  { test: /payment|invoice|deposit|balance|financ|collect/i, type: "Customer Complaint", label: "Payment Not Collected", rationale: "Financial signal in description" },
  { test: /material|ship|backorder|deliver|sku|order|vendor|supplier/i, type: "Material Issue", label: "Material Delay", rationale: "Material / supplier signal in description" },
  { test: /crew|installer|contractor|subcontractor|tech/i, type: "Contractor Issue", label: "Contractor Issue", rationale: "Crew / contractor signal in description" },
  { test: /complain|upset|angry|unhappy|frustrat|customer said/i, type: "Customer Complaint", label: "Customer Communication Gap", rationale: "Customer sentiment signal in description" },
  { test: /install|leak|damage|broken|crack|mis.?cut|wrong size/i, type: "Install Issue", label: "Install Defect", rationale: "Install defect signal in description" },
];

/**
 * Map blocker category → derived classification. Highest-signal source.
 */
function fromBlocker(cat: IssueBlockerCategory | "" | undefined): ClassifyIssueResult | null {
  switch (cat) {
    case "Missing Material":
    case "Damaged Material":
    case "Supplier Delay":
      return { issueType: "Material Issue", label: "Material Delay", rationale: `Blocker: ${cat}`, confident: true };
    case "Permit Rejection":
    case "HOA Documentation":
    case "Inspection Failure":
      return { issueType: "Permit Issue", label: "Permit Stall", rationale: `Blocker: ${cat}`, confident: true };
    case "Crew Unavailable":
      return { issueType: "Contractor Issue", label: "Contractor Issue", rationale: `Blocker: ${cat}`, confident: true };
    case "Customer Unavailable":
      return { issueType: "Scheduling Issue", label: "Access / Scheduling Gap", rationale: `Blocker: ${cat}`, confident: true };
    case "Design Error":
      return { issueType: "Install Issue", label: "Design / Build Error", rationale: `Blocker: ${cat}`, confident: true };
    case "Scheduling Conflict":
    case "Weather Delay":
      return { issueType: "Scheduling Issue", label: "Scheduling Conflict", rationale: `Blocker: ${cat}`, confident: true };
    case "Payment Hold":
      return { issueType: "Customer Complaint", label: "Payment Not Collected", rationale: `Blocker: ${cat}`, confident: true };
    case "Pending Identification":
    case "Other":
    case "":
    case undefined:
      return null;
  }
  return null;
}

/**
 * Map affected area → derived classification. Used as secondary signal.
 */
function fromArea(area: IssueAffectedArea | "" | undefined): ClassifyIssueResult | null {
  switch (area) {
    case "Materials":
      return { issueType: "Material Issue", label: "Material Delay", rationale: `Area: ${area}`, confident: true };
    case "Permit":
      return { issueType: "Permit Issue", label: "Permit Stall", rationale: `Area: ${area}`, confident: true };
    case "Scheduling":
      return { issueType: "Scheduling Issue", label: "Scheduling Conflict", rationale: `Area: ${area}`, confident: true };
    case "Shower Base":
    case "Glass Panel":
    case "Glass Door":
    case "Plumbing":
    case "Tile":
    case "Fixtures":
    case "Vanity":
    case "Framing":
    case "Waterproofing":
    case "Electrical":
      return { issueType: "Install Issue", label: "Install Defect", rationale: `Area: ${area}`, confident: true };
    default:
      return null;
  }
}

/**
 * Public API: derive issue type from real signals. Order:
 *   1) Blocker category (strongest)
 *   2) Description keywords (specific intent)
 *   3) Affected area (fallback)
 *   4) Generic fallback ("Other") — only used when nothing else matches.
 */
export function classifyIssue(input: ClassifyIssueInput): ClassifyIssueResult {
  const blocker = fromBlocker(input.blockerCategory);
  if (blocker) return blocker;

  if (input.description && input.description.trim().length >= 10) {
    for (const k of KEYWORDS) {
      if (k.test.test(input.description)) {
        return { issueType: k.type, label: k.label, rationale: k.rationale, confident: true };
      }
    }
  }

  const area = fromArea(input.affectedArea);
  if (area) return area;

  return {
    issueType: "Other",
    label: "Pending Classification",
    rationale: "Add blocker, area, or details to classify",
    confident: false,
  };
}
